import React, { useEffect, useState } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation
} from 'react-router-dom';
import { hasSupabaseConfig, supabase } from './services/supabase';
import { getSession, setSession, clearSession } from './utils/auth';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import AdminLogin from './pages/AdminLogin';
import PartnerLogin from './pages/PartnerLogin';
import AdminDashboard from './pages/AdminDashboard';
import Content from './pages/Content';
import Partners from './pages/Partners';
import Allocation from './pages/Allocation';
import Requests from './pages/Requests';
import Certificates from './pages/Certificates';
import PartnerDashboard from './pages/PartnerDashboard';
import MyContent from './pages/MyContent';
import RegisterPerson from './pages/RegisterPerson';
import PartnerRequests from './pages/PartnerRequests';
import PartnerCertificates from './pages/PartnerCertificates';
import Settings from './pages/Settings';
import PartnerStudent from './pages/PartnerStudent';

// Page titles shown in the Header, keyed by the last part of the URL (the "active" segment)
const titles = {
  dashboard: 'Dashboard',
  content: 'Courses & Certifications',
  partners: 'Partners',
  allocation: 'Allocation',
  requests: 'Requests',
  certificates: 'Certificates',
  register: 'Register Student',
  settings: 'Settings',
  student: 'My Student'
};

// ============================================================
// AppLayout
// This component renders the Sidebar + Header + the actual page
// content for a LOGGED-IN user (either admin or partner).
// It reads the current URL to decide which sub-page to show.
// ============================================================
function AppLayout({ session, theme, setTheme, logout }) {
  const location = useLocation(); // gives us the current URL path
  const navigate = useNavigate(); // lets us change the URL programmatically
  const isPartner = session.role === 'partner';

  // Extract the last segment of the URL to know which page is "active"
  // e.g. "/academy/admin/content" -> "content"
  const active = location.pathname.split('/').pop() || 'dashboard';

  // Called by Sidebar when user clicks a menu item.
  // Instead of just changing state (old way), we now change the URL.
  const handleNavigate = id => {
    if (id === '__logout') {
      logout();
      return;
    }
    // Build the correct URL based on role: /academy/admin/xxx or /academy/partner/xxx
    navigate(`/academy/${isPartner ? 'partner' : 'admin'}/${id}`);
  };

  return (
    <div className="app">
      {/* Sidebar highlights the "active" item based on current URL */}
      <Sidebar role={session.role} active={active} onNavigate={handleNavigate} />

      <main className="main">
        <Header
          title={titles[active] || 'Dashboard'}
          session={session}
          onLogout={logout}
          theme={theme}
          onToggleTheme={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
        />

        <section className="page">
          {/*
            Nested Routes: these paths are RELATIVE to the parent route
            ("/academy/admin/*" or "/academy/partner/*"), so we only
            write the last segment here, e.g. "content" not "/academy/admin/content"
          */}
          <Routes>
            {isPartner ? (
              // ---- Partner-only pages ----
              <>
                <Route
                  path="dashboard"
                  element={<PartnerDashboard session={session} go={handleNavigate} />}
                />
                <Route path="content" element={<MyContent session={session} />} />
                {/* <Route path="register" element={<RegisterPerson session={session} />} /> */}
                <Route path="requests" element={<PartnerRequests session={session} />} />
                <Route path="student" element={<PartnerStudent session={session} />} />
                <Route
                  path="register"
                  element={
                    <RegisterPerson
                      session={session}
                      onViewStudents={() => navigate('/academy/partner/student')}
                      onRegisterAnother={() => navigate('/academy/partner/register')}
                    />
                  }
                />
                <Route path="certificates" element={<PartnerCertificates session={session} />} />
                {/* Any unknown partner sub-path redirects to the partner dashboard */}
                <Route path="*" element={<Navigate to="/academy/partner/dashboard" replace />} />
              </>
            ) : (
              // ---- Admin-only pages ----
              <>
                <Route path="dashboard" element={<AdminDashboard go={handleNavigate} />} />
                <Route path="content" element={<Content />} />
                <Route path="partners" element={<Partners />} />
                <Route path="allocation" element={<Allocation />} />
                <Route path="requests" element={<Requests />} />
                <Route path="certificates" element={<Certificates />} />
                <Route path="settings" element={<Settings role={session.role} />} />
                {/* Any unknown admin sub-path redirects to the admin dashboard */}
                <Route path="*" element={<Navigate to="/academy/admin/dashboard" replace />} />
              </>
            )}
          </Routes>
        </section>
      </main>
    </div>
  );
}

// ============================================================
// App (root component)
// Sets up BrowserRouter and defines ALL top-level routes:
// - Public login routes (admin/partner)
// - Protected routes (only accessible when logged in)
// - A catch-all redirect for unknown URLs
// ============================================================
export default function App() {
  // session = null means "not logged in". Loaded once from localStorage/cookie via getSession()
  const [session, setAppSession] = useState(getSession());

  // theme = 'light' or 'dark', persisted in localStorage
  const [theme, setTheme] = useState(() => localStorage.getItem('wiiz_theme') || 'light');

  // Whenever theme changes, apply it to the <html> tag and save it
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('wiiz_theme', theme);
  }, [theme]);

  // Listen to Supabase auth state changes (used for partner login via Supabase)
  // We don't act on it directly here; our own session state is the source of truth.
  useEffect(() => {
    if (!hasSupabaseConfig || !supabase) return undefined;
    const { data } = supabase.auth.onAuthStateChange(() => { });
    return () => data.subscription.unsubscribe();
  }, []);

  // Logs the user out: clears Supabase session (if partner) and local session
  const logout = async () => {
    if (session?.role === 'partner' && hasSupabaseConfig && supabase) {
      await supabase.auth.signOut();
    }
    clearSession();
    setAppSession(null);
  };

  // Helper: where should a logged-in user land by default?
  const homeRouteFor = s => `/academy/${s.role === 'partner' ? 'partner' : 'admin'}/dashboard`;

  return (
    <BrowserRouter>
      <Routes>
        {/* ============ PUBLIC ROUTE: Admin Login ============ */}
        <Route
          path="/academy/adminlogin"
          element={
            session ? (
              // Already logged in? Skip the login page, go straight to dashboard
              <Navigate to={homeRouteFor(session)} replace />
            ) : (
              <AdminLoginWrapper theme={theme} setTheme={setTheme} setAppSession={setAppSession} />
            )
          }
        />

        {/* ============ PUBLIC ROUTE: Partner Login ============ */}
        <Route
          path="/academy/partnerlogin"
          element={
            session ? (
              <Navigate to={homeRouteFor(session)} replace />
            ) : (
              <PartnerLoginWrapper theme={theme} setTheme={setTheme} setAppSession={setAppSession} />
            )
          }
        />

        {/* ============ PROTECTED ROUTE: Admin area ============ */}
        {/* The "/*" means this route also matches any nested path like /academy/admin/content */}
        <Route
          path="/academy/admin/*"
          element={
            session && session.role !== 'partner' ? (
              <AppLayout session={session} theme={theme} setTheme={setTheme} logout={logout} />
            ) : (
              // Not logged in as admin? Kick back to admin login
              <Navigate to="/academy/adminlogin" replace />
            )
          }
        />

        {/* ============ PROTECTED ROUTE: Partner area ============ */}
        <Route
          path="/academy/partner/*"
          element={
            session && session.role === 'partner' ? (
              <AppLayout session={session} theme={theme} setTheme={setTheme} logout={logout} />
            ) : (
              <Navigate to="/academy/partnerlogin" replace />
            )
          }
        />

        {/* ============ CATCH-ALL ============ */}
        {/* Any URL that doesn't match above (including just "/") redirects
            either to the user's dashboard (if logged in) or to admin login */}
        <Route
          path="*"
          element={
            <Navigate to={session ? homeRouteFor(session) : '/academy/adminlogin'} replace />
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

// ============================================================
// AdminLoginWrapper
// Small wrapper so the AdminLogin page can call useNavigate()
// (hooks like useNavigate only work INSIDE a component that is
// rendered under <BrowserRouter>, so we can't use it directly
// in the App() function body before render)
// ============================================================
function AdminLoginWrapper({ theme, setTheme, setAppSession }) {
  const navigate = useNavigate();

  // Called by AdminLogin component when login succeeds
  const enter = nextSession => {
    setSession(nextSession);        // save session to localStorage/cookie
    setAppSession(nextSession);     // update React state so app re-renders as logged in
    navigate('/academy/admin/dashboard'); // change URL to the admin dashboard
  };

  return (
    <AdminLogin
      onLogin={enter}
      onSwitchToPartner={() => navigate('/academy/partnerlogin')}
      theme={theme}
      onToggleTheme={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
    />
  );
}

// ============================================================
// PartnerLoginWrapper
// Same idea as above, but for the Partner login flow
// ============================================================
function PartnerLoginWrapper({ theme, setTheme, setAppSession }) {
  const navigate = useNavigate();

  const enter = nextSession => {
    setSession(nextSession);
    setAppSession(nextSession);
    navigate('/academy/partner/dashboard');
  };

  return (
    <PartnerLogin
      onLogin={enter}
      onSwitchToAdmin={() => navigate('/academy/adminlogin')}
      theme={theme}
      onToggleTheme={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
    />
  );
}